import React, {FC, useCallback, useState} from "react";
import {ScrollView, StyleSheet, Text, View} from "react-native";
import {NavigationProp, useNavigation} from "@react-navigation/native";

import {Button} from "../../components";
import {ButtonType} from "../../theme";
import {AppTheme, useTheme} from "../../theme/colors";
import {HomeTabsParams} from "../../navigation/AppStack/HomeTabParams";
import {ProfileStackRoute} from "../../navigation/AppStack/routes";
import PageContainer from "../../components/PageContainer";
import CustomAlert from "../../components/CoustmizeAlertmsg";
import useAuth from "../../contexts/Auth/useAuth";
import {ProfileScreenRoute} from "../../navigation/ProfileStack/routes";
import {deleteUserAccount, getSession} from "../../utils/cognitoAuth"; // Import utility functions

const DeleteUserScreen: FC = () => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [isAlertVisible, setAlertVisibility] = useState(false);
    const navigation = useNavigation<NavigationProp<HomeTabsParams>>();
    const {user} = useAuth();

    const deleteUser = useCallback(async () => {
        if (!user) return;
        try {
            setShowLoader(true);

            // Get a valid JWT token (refresh if expired)
            const session = await getSession();
            if (!session || !session.idToken) {
                throw new Error("Failed to retrieve a valid session.");
            }

            console.warn(" Deleting user account " + user.username);
            // Delete user with a valid token
            await deleteUserAccount(user.username, session.idToken);
        } catch (error) {
            console.error("❌ Error deleting user:", error);
            setShowLoader(false);
            setAlertVisibility(true);
        }
    }, [user]);

    const cancelDeleteUser = useCallback(() => {
        navigation.navigate(ProfileStackRoute, {
            screen: ProfileScreenRoute
        });
    }, [navigation]);

    return (
      <>
          {showLoader ? (
            <PageContainer loading/>
          ) : (
            <View style={styles.viewContainer}>
                <ScrollView
                  style={{flex: 1}}
                  scrollEventThrottle={1600}
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.scrollContent}
                >
                    <View style={styles.textContainer}>
                        <Text style={styles.descriptiveText}>
                            Are you sure you want to delete your account? All data related to
                            your account will be lost.
                        </Text>
                    </View>
                    <View style={styles.buttonContainer}>
                        <Button
                          variant={ButtonType.primary}
                          bgColor={theme.ERROR}
                          title="Delete my account"
                          onPress={deleteUser}
                        />
                        <Button
                          variant={ButtonType.secondary}
                          bgColor={theme.LIGHT_GREY}
                          title="Cancel"
                          onPress={cancelDeleteUser}
                        />
                    </View>
                    <CustomAlert
                      cancelText="Cancel"
                      confirmText="OK"
                      visible={isAlertVisible}
                      showCancelButton={false}
                      title=""
                      message="Error deleting user. Please try again."
                      onCancel={() => setAlertVisibility(false)}
                      onConfirm={() => setAlertVisibility(false)}
                    />
                </ScrollView>
            </View>
          )}
      </>
    );
};

export default DeleteUserScreen;

const getStyles = (theme: AppTheme) =>
  StyleSheet.create({
      viewContainer: {
          flex: 1,
          backgroundColor: theme.BACKGROUND,
          alignItems: "center",
          justifyContent: "center",
      },
      scrollContent: {
          flexGrow: 1,
          justifyContent: "center",
          alignItems: "center",
          padding: 20,
      },
      textContainer: {
          alignItems: "center",
          maxWidth: 480,
          padding: 20,  // Added padding around the text
          borderRadius: 10, // Optional: Rounded corners for better UI
      },
      descriptiveText: {
          fontSize: 20,
          fontWeight: "400",
          textAlign: "center",
          lineHeight: 25,
      },
      buttonContainer: {
          gap: 10,
          paddingVertical: 5,
          alignItems: "center",
      },
  });